import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useUpdateAuthToken } from '@/features/authentication-update-token/use-update-auth-token';
import { WRAPPER_TO_GAME_CLIENT_EVENT_TYPES } from '@/features/event-handlers/wrapper-event-handlers/game-client-events/types';
import { useGameHelpers } from '@/features/game/hooks/use-game-helpers';
import { isEmbeddedInGameWindowV3 } from '@/features/game-context/platform-helpers';
import {
  gameContextSelector,
  setGameWindowUIEnabled,
} from '@/features/game-context/store/game-context';
import { useGeolocation } from '@/features/geolocation/use-geolocation';
import { useGeolocationCallback } from '@/features/geolocation/use-geolocation-callback';
import { isLobbyEmbeddedQuery } from '@/features/wrapper-proxy/resolve-launcher-message-origin';

export const useHandleWrapperToGameClientEvent = () => {
  const dispatch = useDispatch();
  const { wrapperResponseHandler } = useGeolocationCallback();
  const { updateAuthToken } = useUpdateAuthToken();

  const { initialized } = useSelector(gameContextSelector);

  const { triggerGeolocation } = useGeolocation();
  const {
    initializeGameClient,
    initializeClientHandler,
    iosSoftReload,
    initializeGameURL,
    resumeDetachedSession,
  } = useGameHelpers(triggerGeolocation);

  useEffect(() => {
    initializeGameClient();
  }, [initializeGameClient]);

  useEffect(() => {
    if (initialized && !isLobbyEmbeddedQuery()) {
      triggerGeolocation();
    } else if (isLobbyEmbeddedQuery()) {
      triggerGeolocation();
    }
  }, [triggerGeolocation, initialized]);

  function handleAuthenticationTokenUpdate(event: MessageEvent) {
    updateAuthToken(event);
  }

  function handleGameWindowUIEnabled(event: MessageEvent) {
    dispatch(setGameWindowUIEnabled(Boolean(event?.data?.payload)));
  }

  function mapGameClientEvent(event: MessageEvent) {
    switch (event?.data?.type) {
      case WRAPPER_TO_GAME_CLIENT_EVENT_TYPES.GEOLOCATION_MESSAGE:
      case WRAPPER_TO_GAME_CLIENT_EVENT_TYPES.GEOLOCATION_UPDATE:
        // In the v3 game-window the shell owns geolocation, so ignore forwarded
        // geo here to avoid a redundant client-side geo flow. Other consumers
        // (v2 launcher, iOS) still process it.
        if (!isEmbeddedInGameWindowV3()) {
          wrapperResponseHandler(event);
        }
        break;
      case WRAPPER_TO_GAME_CLIENT_EVENT_TYPES.INITIALIZE_PARAMS:
        initializeClientHandler(event);
        break;
      case WRAPPER_TO_GAME_CLIENT_EVENT_TYPES.INITIALIZE_URL:
        initializeGameURL(event);
        break;
      case WRAPPER_TO_GAME_CLIENT_EVENT_TYPES.TRIGGER_GEOLOCATION:
        initializeClientHandler(event);
        break;
      case WRAPPER_TO_GAME_CLIENT_EVENT_TYPES.AUTHENTICATION_TOKEN_UPDATE:
        handleAuthenticationTokenUpdate(event);
        break;
      case WRAPPER_TO_GAME_CLIENT_EVENT_TYPES.IOS_SOFT_RELOAD:
        iosSoftReload(event);
        break;
      case WRAPPER_TO_GAME_CLIENT_EVENT_TYPES.GAME_WINDOW_UI_ENABLED:
        handleGameWindowUIEnabled(event);
        break;
      case WRAPPER_TO_GAME_CLIENT_EVENT_TYPES.RESUME_DETACHED_SESSION:
        resumeDetachedSession(event);
        break;
      default:
        break;
    }
  }

  return { mapGameClientEvent };
};
